import { ref, type Ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import type { PageOrientation } from '@record-fill';
// Direct path on purpose: FormBuilderLayout.test.ts stubs the '@/shared/ui' barrel with only
// ColoredButton, so importing from the barrel would resolve to undefined in that suite.
import ConfirmCloseModal from '@/shared/ui/modals/ConfirmCloseModal.vue';
import { matrixBuilderApi } from '../api/form-builder.api';
import {
  buildSendToApprovalPayload,
  isApprovalFormComplete,
  type ApprovalFormValues,
} from '../lib/approval-submission';
import { findPermissionsSubmitProblem } from '../lib/permissions-submit-check';
import type { FormElement } from '../model/form.types';
import { useFormBuilderToast } from './useFormBuilderToast';

const SUBMIT_ERROR_FALLBACK = 'Erro ao enviar Registro para Aprovação, tente novamente.';

/**
 * Business rules the backend rejects with a 400 (prefixo em homologação, gestor inelegível,
 * rascunho já enviado) carry a message the user needs to read. Anything else keeps the generic
 * fallback. A network failure must not surface as "network".
 */
function extractValidationMessage(error: unknown): string | undefined {
  if (!isAxiosError(error) || error.response?.status !== 400) return undefined;

  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
}

const EXIT_CONFIRM_DESCRIPTION =
  'Sair desta tela cancela todas as ações realizadas na página. A ação não pode ser desfeita.';

export interface UseFormBuilderApprovalStepDependencies {
  /** The draft being edited, or null in create mode. */
  getMatrixId: () => number | null;
  getElements: () => FormElement[];
  getRulesStructure: () => object | null;
  getPermissionsStructure: () => object | null;
  getOrientation: () => PageOrientation;
  returnToPermissions: () => void;
  onSubmitted: () => void;
}

export function useFormBuilderApprovalStep(deps: UseFormBuilderApprovalStepDependencies): {
  isSubmittingApproval: Ref<boolean>;
  confirmLeaveApprovalStep: (onConfirmed?: () => void) => void;
  confirmCancelApprovalStep: () => void;
  submitApproval: (values: ApprovalFormValues) => Promise<void>;
} {
  const { showSuccessToast, showErrorToast, showWarningToast } = useFormBuilderToast();
  const isSubmittingApproval = ref(false);

  /**
   * `onConfirmed` existe porque a mesma confirmacao serve a dois destinos: os botoes da tela voltam
   * para a etapa de Permissoes, e a guarda de rota precisa seguir para a rota pedida.
   */
  function openApprovalConfirmModal(title: string, onConfirmed?: () => void) {
    const { open, close } = useModal({
      component: ConfirmCloseModal,
      attrs: {
        clickToClose: false,
        escToClose: false,
        title,
        description: EXIT_CONFIRM_DESCRIPTION,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          if (onConfirmed) {
            onConfirmed();
            return;
          }
          deps.returnToPermissions();
        },
      },
    });

    open();
  }

  function confirmLeaveApprovalStep(onConfirmed?: () => void) {
    openApprovalConfirmModal('Tem certeza que deseja sair desta tela?', onConfirmed);
  }

  function confirmCancelApprovalStep() {
    openApprovalConfirmModal('Tem certeza que deseja cancelar?');
  }

  async function sendToApproval(values: ApprovalFormValues) {
    await matrixBuilderApi.sendToApproval(
      buildSendToApprovalPayload({
        values,
        elements: deps.getElements(),
        rulesStructure: deps.getRulesStructure(),
        permissionsStructure: deps.getPermissionsStructure(),
        orientation: deps.getOrientation(),
        matrixId: deps.getMatrixId(),
      })
    );

    showSuccessToast({
      title: 'Registro enviado para aprovação',
      message: 'Os usuários envolvidos serão notificados.',
      type: 'success',
    });

    deps.onSubmitted();
  }

  function showSubmitError(error: unknown) {
    console.error('Error sending matrix to approval:', error);
    showErrorToast({
      title: extractValidationMessage(error) ?? SUBMIT_ERROR_FALLBACK,
      type: 'error',
      hideIcon: true,
    });
  }

  async function submitApproval(values: ApprovalFormValues) {
    if (isSubmittingApproval.value) return;

    if (!isApprovalFormComplete(values)) {
      showWarningToast({
        title: 'Ops! Parece que há campos obrigatórios não preenchidos.',
        type: 'warning',
        hideIcon: true,
      });
      return;
    }

    const permissionsProblem = findPermissionsSubmitProblem(
      deps.getPermissionsStructure(),
      deps.getElements()
    );
    if (permissionsProblem !== null) {
      showWarningToast({ title: permissionsProblem, type: 'warning', hideIcon: true });
      return;
    }

    isSubmittingApproval.value = true;
    try {
      await sendToApproval(values);
    } catch (error) {
      showSubmitError(error);
    } finally {
      isSubmittingApproval.value = false;
    }
  }

  return {
    isSubmittingApproval,
    confirmLeaveApprovalStep,
    confirmCancelApprovalStep,
    submitApproval,
  };
}
